import { FC, ReactNode } from 'react';
import { useRoleAccess } from '@/hooks/useRoleAccess';
import { Role } from '@/types/Role';
import { Navigate, useLocation } from 'react-router-dom';
import { ROUTES } from '@/route/routes.constants';
import { ROLE } from '@/role.constant';
import { POST_LOGIN_REDIRECT_STORAGE_KEY } from '@/lib/auth/post-login-redirect';

/** Role that bypasses all route-level role and permission checks */
const ADMIN_BYPASS_ROLE = ROLE.ADMINISTRATOR;

export interface RequiredPermission {
  resource: string;
  action: string;
}

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRoles?: Role[];
  requiredAnyRole?: Role[];
  requiredAllRoles?: Role[];
  requiredPermission?: RequiredPermission;
  fallback?: ReactNode;
}

function getForbiddenContent(fallback?: ReactNode): ReactNode {
  return fallback != null ? <>{fallback}</> : <Navigate to={ROUTES.FORBIDDEN} replace />;
}

type AccessChecks = Pick<
  ProtectedRouteProps,
  'requiredRoles' | 'requiredAnyRole' | 'requiredAllRoles' | 'requiredPermission'
> & {
  canAccessCurrentRoute: () => boolean;
  hasRole: (role: Role) => boolean;
  hasAnyRole: (roles: Role[]) => boolean;
  hasAllRoles: (roles: Role[]) => boolean;
  hasPermission: (resource: string, action: string) => boolean;
};

function hasExplicitRoleRequirement(checks: AccessChecks): boolean {
  return (
    checks.requiredRoles != null ||
    checks.requiredAnyRole != null ||
    checks.requiredAllRoles != null
  );
}

function satisfiesAccessRequirements(checks: AccessChecks): boolean {
  const {
    canAccessCurrentRoute,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    hasPermission,
    requiredRoles,
    requiredAnyRole,
    requiredAllRoles,
    requiredPermission,
  } = checks;

  // When the route declares explicit role requirements, those are the source of truth.
  // The per-role allowedRoutes list is only used for routes without explicit role props.
  if (!hasExplicitRoleRequirement(checks) && !canAccessCurrentRoute()) {
    return false;
  }
  if (requiredRoles != null && !requiredRoles.every((role) => hasRole(role))) return false;
  if (requiredAnyRole != null && !hasAnyRole(requiredAnyRole)) return false;
  if (requiredAllRoles != null && !hasAllRoles(requiredAllRoles)) return false;
  if (
    requiredPermission != null &&
    !hasPermission(requiredPermission.resource, requiredPermission.action)
  ) {
    return false;
  }
  return true;
}

export const ProtectedRoute: FC<ProtectedRouteProps> = ({
  children,
  requiredRoles,
  requiredAnyRole,
  requiredAllRoles,
  requiredPermission,
  fallback,
}) => {
  const access = useRoleAccess();
  const location = useLocation();

  if (!access.isAuthenticated) {
    const returnPath = `${location.pathname}${location.search}`;
    if (returnPath && returnPath !== ROUTES.ROOT) {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_STORAGE_KEY, returnPath);
    }
    return <Navigate to={ROUTES.ROOT} state={{ from: location }} replace />;
  }

  if (access.hasRole(ADMIN_BYPASS_ROLE)) {
    return <>{children}</>;
  }

  const hasAccess = satisfiesAccessRequirements({
    ...access,
    requiredRoles,
    requiredAnyRole,
    requiredAllRoles,
    requiredPermission,
  });
  if (!hasAccess) {
    return getForbiddenContent(fallback);
  }

  return <>{children}</>;
};
